import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Role, Team } from 'api/models';
import { Form } from 'components/atoms/form';
import { Option } from 'components/molecules/squad-picker/types';
import { toastService } from 'components/molecules/toast';
import {
  RoleFormValues,
  RolesDrawer,
  RoleSettingsTab,
  RolesForm,
} from 'components/organisms/module-settings/roles';
import { useTranslate } from 'hooks/use-translation';
import {
  getTeamsInAccountQueryKey,
  useCreatePlaceholderMutation,
} from 'queries/placeholder';
import { ROLES_QUERY_KEY, useCreateRoleMutation } from 'queries/roles';
import { add as addTeam } from 'stores/teams';
import { getRoleTypeForTab, getTabForRoleType } from '.';

interface Props {
  roles: Role[];
  view: RoleSettingsTab;
  placeholders: { [id: number]: Team };
  onClose: VoidFunction;
  switchTab: (tab: RoleSettingsTab) => void;
}

export const CreateRole: FC<Props> = ({ roles, view, onClose, switchTab }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRoleMutation({
      onSuccess: async () => {
        queryClient.invalidateQueries(ROLES_QUERY_KEY);
      },
    });

  const { mutate: createPlaceholder, isLoading: isCreatingPlaceholder } =
    useCreatePlaceholderMutation({
      onSuccess: async (team) => {
        dispatch(addTeam(team));
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(({ teamId }) => teamId !== team.teamId),
            team,
          ],
        );
      },
    });

  const onSubmit = async () => {
    const formValues: RoleFormValues = await form.validateFields();

    try {
      const newRole: Role = await createRole(formValues);

      if (formValues.use_as_placeholder) {
        await createPlaceholder({
          name: newRole.roleName,
          role: {
            value: newRole.roleId,
            label: newRole.roleName,
            extra: {
              type: newRole.type,
            },
          } as unknown as Option,
        });
      }
      if (getRoleTypeForTab(view) !== newRole.type) {
        switchTab(getTabForRoleType(newRole.type));
      }
      toastService({
        title: translate('settings.role.created.success.title'),
        message: translate('settings.role.created.success.message'),
      }).success();
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      use_as_placeholder: true,
      type: getRoleTypeForTab(view),
    });
  }, [form, translate, view]);

  return (
    <RolesDrawer
      title={<FormattedMessage id="settings.roles.new" />}
      onClose={onClose}
      onSave={onSubmit}
      isSubmitting={isCreatingRole || isCreatingPlaceholder}
      form={<RolesForm roles={roles} form={form} view={view} />}
    />
  );
};
